import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import KNCallToAction from 'components/KNCallToAction/KNCallToAction'
import KNFormDropzone from 'components/KNForm/KNFormDropzone'
import KNLoader from 'components/KNLoader/KNLoader'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import BusinessEntitiesList from 'screens/CarrierProfile/BusinessEntititesList'
import { maxFileSize, minFileSize } from 'screens/StatusManager/StatusManager.service'
import {
  businessEntitiesQueryOptions,
  CARRIER_LOGO_QUERY_KEY,
  carrierLogoQueryOptions,
  carrierProfileQueryOptions,
  deleteCarrierLogo,
  editCarrierLogo,
} from './CarrierProfile.service'
import type { CarrierProfileFormValues } from './CarrierProfile.types'
import EditCompanyDetailsDialog from './EditCompanyDetailsDialog'
import CreateOrEditBusinessEntityDialog from './CreateOrEditBusinessEntityDialog'
import SelectQualificationsDialog from './SelectQualificationsDialog'
import SelectTransportModesDialog from './SelectTransportModesDialog'
import SelectTypesDialog from './SelectTypesDialog'

const CompanyTab = (): ReactElement => {
  const { t } = useTranslation()
  const [carrierProfileQuery, carrierLogoQuery, businessEntitiesQuery] = useQueries({
    queries: [carrierProfileQueryOptions, carrierLogoQueryOptions, businessEntitiesQueryOptions],
  })
  const carrierProfile = carrierProfileQuery.data
  const carrierLogo = carrierLogoQuery.data
  const businessEntities = businessEntitiesQuery.data ?? []
  const queryClient = useQueryClient()
  const editCarrierLogoMutation = useMutation({
    mutationFn: editCarrierLogo,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CARRIER_LOGO_QUERY_KEY] }),
  })
  const deleteCarrierLogoMutation = useMutation({
    mutationFn: deleteCarrierLogo,
    onSuccess: () => queryClient.resetQueries({ queryKey: [CARRIER_LOGO_QUERY_KEY] }),
  })
  const editCompanyDetailsDialog = useDialog<typeof EditCompanyDetailsDialog>()
  const selectTypesDialog = useDialog<typeof SelectTypesDialog>()
  const selectTransportModesDialog = useDialog<typeof SelectTransportModesDialog>()
  const selectQualificationsDialog = useDialog<typeof SelectQualificationsDialog>()
  const createBusinessEntityDialog = useDialog<typeof CreateOrEditBusinessEntityDialog>()

  const handleImageUpload = async (file: File): Promise<File> => {
    await editCarrierLogoMutation.mutateAsync(file)
    return file
  }

  const handleImageDeletion = async (): Promise<void> => {
    await deleteCarrierLogoMutation.mutateAsync()
  }

  const form = useForm<CarrierProfileFormValues>({
    criteriaMode: 'all',
  })

  if (carrierProfileQuery.isPending || carrierLogoQuery.isPending || businessEntitiesQuery.isPending) {
    return (
      <KNLoader />
    )
  }

  return (
    <FormProvider {...form}>
      {carrierProfile ? (
        <Stack spacing={4}>
          <Grid container spacing={4}>
            <Grid size={12}>
              {carrierLogo ? (
                <Stack spacing={1}>
                  <img
                    src={carrierLogo}
                    alt={t('screens.cs.carrier_profile.company.logo')}
                    style={{ maxWidth: '72px', maxHeight: '72px', borderRadius: 8 }}
                  />
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleImageDeletion}
                    sx={{ padding: 0, alignSelf: 'flex-start' }}
                  >
                    {t('general.delete')}
                  </Button>
                </Stack>
              ) : (
                <KNFormDropzone
                  label={t('screens.cs.carrier_profile.company.logo')}
                  name="companyLogo"
                  control={form.control}
                  accept={['image/jpeg', 'image/png']}
                  size="small"
                  processFile={handleImageUpload}
                  minFileSize={minFileSize}
                  maxFileSize={maxFileSize}
                  sx={{ width: '15rem' }}
                />
              )}
            </Grid>
            <Grid size={3}>
              <Stack spacing={1}>
                <Typography variant="p6" color="primary.main">
                  {t('screens.cs.carrier_profile.company.company_details')}
                </Typography>
                <Box>
                  <Typography variant="p2" color="primary.main">{carrierProfile.name}</Typography>
                  <Typography color="primary.main">{carrierProfile.address}</Typography>
                  <Typography color="primary.main">{carrierProfile.city}</Typography>
                  <Typography color="primary.main">{carrierProfile.country}</Typography>
                  {carrierProfile.registrationTaxNumber && (
                    <Typography color="primary.main">{carrierProfile.registrationTaxNumber}</Typography>
                  )}
                  {carrierProfile.website && (
                    <Typography color="primary.main">{carrierProfile.website}</Typography>
                  )}
                </Box>
                {carrierProfile.additionalNotes && (
                  <>
                    <Typography variant="p6" color="primary.main">
                      {t('screens.cs.carrier_profile.company.additional_notes')}
                    </Typography>
                    <Typography color="primary.main">{carrierProfile.additionalNotes}</Typography>
                  </>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={editCompanyDetailsDialog.handleOpen}
                  sx={{ alignSelf: 'start' }}
                >
                  {t('screens.cs.carrier_profile.company.edit_company_details')}
                </Button>
              </Stack>
            </Grid>
            <Grid size={3}>
              <Stack spacing={1}>
                <Typography variant="p6" color="primary.main">
                  {t('screens.cs.carrier_profile.company.types')}
                </Typography>
                <Box>
                  {carrierProfile.types.map((type) => (
                    <Typography key={type} color="primary.main">{t(`screens.cs.carrier_profile.types.${type}`)}</Typography>
                  ))}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={selectTypesDialog.handleOpen}
                  sx={{ alignSelf: 'start' }}
                >
                  {t('screens.cs.carrier_profile.company.select_types')}
                </Button>
              </Stack>
            </Grid>
            <Grid size={3}>
              <Stack spacing={1}>
                <Typography variant="p6" color="primary.main">
                  {t('screens.cs.carrier_profile.company.transport_modes')}
                </Typography>
                <Box>
                  {carrierProfile.transportModes.map((transportMode) => (
                    <Typography key={transportMode} color="primary.main">{t(`screens.cs.carrier_profile.transport_modes.${transportMode}`)}</Typography>
                  ))}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={selectTransportModesDialog.handleOpen}
                  sx={{ alignSelf: 'start' }}
                >
                  {t('screens.cs.carrier_profile.company.select_transport_modes')}
                </Button>
              </Stack>
            </Grid>
            <Grid size={3}>
              <Stack spacing={1}>
                <Typography variant="p6" color="primary.main">
                  {t('screens.cs.carrier_profile.company.qualifications')}
                </Typography>
                <Box>
                  {carrierProfile.qualifications.map((qualification) => (
                    <Typography key={qualification} color="primary.main">{t(`screens.cs.carrier_profile.qualifications.${qualification}`)}</Typography>
                  ))}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={selectQualificationsDialog.handleOpen}
                  sx={{ alignSelf: 'start' }}
                >
                  {t('screens.cs.carrier_profile.company.select_qualifications')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Stack spacing={1}>
            <Typography variant="p6" color="primary.main">
              {t('screens.cs.carrier_profile.company.business_entities')}
            </Typography>
            <BusinessEntitiesList businessEntities={businessEntities} />
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={createBusinessEntityDialog.handleOpen}
              sx={{ alignSelf: 'start' }}
            >
              {t('screens.cs.carrier_profile.business_entities.add_business_entity')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <KNCallToAction
          header={t('screens.cs.carrier_profile.company.empty_header')}
          content={t('screens.cs.carrier_profile.company.empty_content')}
          sx={{ minHeight: '20rem' }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={editCompanyDetailsDialog.handleOpen}
          >
            {t('screens.cs.carrier_profile.company.add_company_details')}
          </Button>
        </KNCallToAction>
      )}
      <EditCompanyDetailsDialog
        payload={carrierProfile ? {
          carrierProfile,
        } : undefined}
        open={editCompanyDetailsDialog.isOpen}
        onAction={editCompanyDetailsDialog.handleAction}
        onClose={editCompanyDetailsDialog.handleClose}
      />
      {carrierProfile && (
        <>
          <SelectTypesDialog
            payload={{
              carrierProfile: carrierProfile,
            }}
            open={selectTypesDialog.isOpen}
            onAction={selectTypesDialog.handleAction}
            onClose={selectTypesDialog.handleClose}
          />
          <SelectTransportModesDialog
            payload={{
              carrierProfile: carrierProfile,
            }}
            open={selectTransportModesDialog.isOpen}
            onAction={selectTransportModesDialog.handleAction}
            onClose={selectTransportModesDialog.handleClose}
          />
          <SelectQualificationsDialog
            payload={{
              carrierProfile: carrierProfile,
            }}
            open={selectQualificationsDialog.isOpen}
            onAction={selectQualificationsDialog.handleAction}
            onClose={selectQualificationsDialog.handleClose}
          />
          <CreateOrEditBusinessEntityDialog
            open={createBusinessEntityDialog.isOpen}
            onAction={createBusinessEntityDialog.handleAction}
            onClose={createBusinessEntityDialog.handleClose}
          />
        </>
      )}
    </FormProvider>
  )
}

export default CompanyTab
